// src/com/user_alloc/UserAlloc.js
import React, { useEffect, useRef, useState } from 'react';
//import { styled } from '@mui/material/styles';
import { CircularProgress, IconButton } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PageviewIcon from '@mui/icons-material/Pageview';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {ContentConainer, ContentHeader, ControlBox, ToolBox} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import FlexyTable from '../../common/flexytable/FlexyTable';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { authTableTreeRepo, userAllocListRepo } from '../../value/AppModel';
import {
    LIST_USER_ALLOC, GET_USER_ALLOC,
    REMOVE_USER_ALLOC, PERMIT_USER_ALLOC
} from './UserAllocGql';
import { UserAllocDictionary } from './UserAllocDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import UserAllocView from './UserAllocView';
import UserAllocPermit from './UserAllocPermit';

/**
 * (USER_ALLOC) 정보 View Controller
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-09-23 14:06:57 using DbdbTap
 */
export default function UserAlloc({
    viewServiceTags // for switching view
}) {
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [viewerState, setViewerState] = useState(AppObject.EmptyViewerState);
    const [permitFormState, setPermitFormState] = useState(AppObject.EmptyViewerState);
    const userAllocListData = useReactiveVar(userAllocListRepo);
    const [searchText, setSearchText] = useState('');
    const checkAuth = useAuthorizationCheck();
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);
    const isEditingRef = useRef(false); // 플래그 생성

    // ##### Call GraphQL to get List #####
    const [getUserAllocList, responseList] = useLazyQuery(LIST_USER_ALLOC, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: UserAllocDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getUserAllocItem, responseItem] = useLazyQuery(GET_USER_ALLOC, {
		...AppObject.NoCachedFetch,
		onCompleted: (data, option) => {onCompleteGetItem(data, option)},
		onError: (error) => addAlert({title: UserAllocDictionary.errorTitle.Get, message: [error.message]})
	});
    
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###

    const [removeUserAlloc, responseRemove] = useMutation( REMOVE_USER_ALLOC, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: UserAllocDictionary.errorTitle.Remove, message: [error.message]})
	});

    const [permitUserAlloc, responsePermit] = useMutation( PERMIT_USER_ALLOC, {
        onCompleted: (data, option) => onCompletePermit(data, option),
        onError: (error) => addAlert({title: UserAllocDictionary.errorTitle.Permit, message: [error.message]})
    });

    const getUserAllocListNow = () => {
        getUserAllocList();
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        const diff = new Date().getTime()/1000 - userAllocListData.when;
        if(diff > 300)
            getUserAllocListNow();
    }, []);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.userAllocList) {
            userAllocListRepo({when:new Date().getTime()/1000, records:data.userAllocList});
        }
    };

    const onCompleteGetItem = (data, option) => {
        if(data.userAllocItem) {
            if(isEditingRef.current) setPermitFormState({open:true, item:data.userAllocItem});
            else setViewerState({open:true, item:data.userAllocItem});
        }
        else addAlert({title:UserAllocDictionary.errorTitle.Get, message:['승인요청 정보를 찾을 수 없습니다.']});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.userAllocRemove.ok) getUserAllocListNow();
        else addAlert({title:UserAllocDictionary.errorTitle.Remove, message:[data.userAllocRemove.message]});
    };

    const onCompletePermit = (data, clientOption) => {
        if(data.userAllocPermit.ok) {
            getUserAllocListNow();
            setPermitFormState(AppObject.EmptyViewerState);
        }
        else addAlert({title:UserAllocDictionary.errorTitle.Permit, message:[data.userAllocPermit.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 60) {
            enqueueSnackbar(AppWord.ReloadNotInOneMinute, {variant:'info', autoHideDuration:2000});
        }
        else getUserAllocListNow();
    };

    const onRequestOpenPermitForm = (item) => {
        isEditingRef.current = true;
        getUserAllocItem({variables:{userAllocSeq:item.userAllocSeq}});
    };

    const onRequestPermit = (item) => {
        permitUserAlloc({variables:{userAlloc:item}});
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "승인요청 정보 삭제",
            content: [
                `승인요청(${item.userName})을 삭제하시겠습니까?`,
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeUserAlloc({variables:{userAlloc:{userAllocSeq:item.userAllocSeq}}});
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onDoubleClickRow = (record) => {
        isEditingRef.current = false;
        getUserAllocItem({variables:{userAllocSeq:record.userAllocSeq}});
    };

    const onCloseView = () => {
        setViewerState(AppObject.EmptyViewerState);
    };

    const onRequestOpenPermitFormFromViewer = (item) => {
        setViewerState(AppObject.EmptyViewerState);
        setPermitFormState({open: true, item: item});
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.EDIT: onRequestOpenPermitForm(row); break;
            case AppObject.command.DELETE: confirmRemove(row); break;
            case AppObject.command.VIEW: onDoubleClickRow(row); break;
            default: return;
        }
    };

    const canPermit = checkAuth(authTableTree.tree.pr_user_alloc_permit);
    const tools = [
        {id:AppObject.command.VIEW, icon:<PageviewIcon/> }
    ];
    
    if(canPermit) {
            tools.push(
            {id:AppObject.command.EDIT, icon:<VerifiedUserIcon/>}, // 승인요청 허용 또는 거부.
            {id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning},
        );
    }

    return(
        <ContentConainer>
            {viewServiceTags}
            <ContentHeader>
                <ControlBox>
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    />
                </ControlBox>
                <ToolBox>
                    {
                        responseRemove.loading ||
                        responseList.loading ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={UserAllocDictionary.tableProps.tableId}
                tableProps={UserAllocDictionary.tableProps}
                records={userAllocListData.records}
                search={searchText}
                tools={tools}
                onClickTool={onClickToolOnRow}
                onRowDoubleClicked={onDoubleClickRow}
            />
            <UserAllocView
                open={viewerState.open}
                item={viewerState.item}
                onRequestPermit={onRequestOpenPermitFormFromViewer}
                onClose={onCloseView}
            />
            <UserAllocPermit
                open={permitFormState.open}
                item={permitFormState.item}
                onRequestPermit={onRequestPermit}
                onClose={()=>setPermitFormState(AppObject.EmptyViewerState)}
            />
        </ContentConainer>
    );
}
